import { Canceler } from 'axios';
import { action, computed, observable, toJS, makeObservable } from 'mobx';
import { defaultPagination, PER_PAGE } from 'src/constants/main';
import { modalStore } from 'src/mobx/modalStore';
import client from 'src/services/client';
import { IDealItem } from 'src/types/combo';
import { IPagination, MODALS_CRUMB } from 'src/types/main';
import { IProductV4, PRODUCT_TYPE } from 'src/types/product';
import { arrayMerge } from 'src/utils/formatter';
import { getCancelToken } from 'src/utils/helpers';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import logger from '~/services/logger';

class ItemsForDealModalStore {
  constructor() {
    makeObservable(this);
  }

  cancel: Canceler | null = null;

  @observable loading: boolean = false;

  @observable activeCrumb: MODALS_CRUMB = MODALS_CRUMB.ALL;
  @observable searchValue: string = '';

  @observable selectedItems: IDealItem[] = [];
  @observable selectedCategories: IPickerOption[] = [];

  @observable pagination: IPagination<IProductV4> = defaultPagination;

  @computed get isMulti(): boolean {
    return Boolean(modalStore.itemsForDealModal?.isMulti);
  }

  @computed get isSelectedCrumb(): boolean {
    return this.activeCrumb === MODALS_CRUMB.SELECTED;
  }

  public initStore = () => {
    this.loading = false;
    this.activeCrumb = MODALS_CRUMB.ALL;
    this.searchValue = '';
    this.selectedItems = toJS(modalStore.itemsForDealModal?.selectedItems) || [];
    this.selectedCategories = [];
    this.pagination = defaultPagination;

    this.fetchItems(1);
  };

  @action fetchItems = async (page: number = this.pagination.current_page + 1) => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      if (this.isSelectedCrumb) return;
      this.loading = true;

      const pagination: IPagination<IProductV4> = await client.get(`/v4/products`, {
        params: {
          page,
          name: this.searchValue,
          categories: this.fetchCategoriesParams,
          types: [PRODUCT_TYPE.PRODUCT],
          only_for_combo: 1,
          is_delete: 0,
        },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });

      this.pagination = pagination;
      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'ItemsForDealModalStore fetchItems' });
    }
  };
  private transformItem = (item: IProductV4 | IDealItem): IDealItem => {
    return {
      ...item,
      category_id: item.category_id || (item as IProductV4).category?.id,
      category_status: (item as IDealItem).category_status || (item as IProductV4).category?.status,
      product_price: item.price,
    };
  };
  private get fetchCategoriesParams(): (string | number)[] | undefined {
    return this.selectedCategories.map((category) => category.value);
  }

  @action changeCrumb = (crumb: MODALS_CRUMB) => {
    this.activeCrumb = crumb;
    this.fetchItems(1);
  };

  @action selectCategory = (options: IPickerOption[]) => {
    this.selectedCategories = options;
    this.fetchItems(1);
  };

  @action onSearch = (search: string) => {
    this.searchValue = search;
    this.fetchItems(1);
  };

  @action selectItem = (item: IProductV4 | IDealItem) => {
    const alreadySelected: boolean = this.selectedItems.some((_item) => _item.id === item.id);

    if (alreadySelected) {
      this.selectedItems = this.selectedItems.filter((_item) => item.id !== _item.id);
      return;
    }

    if (this.isMulti) {
      this.selectedItems = this.selectedItems.concat(this.transformItem(item));
      return;
    }

    this.selectedItems = [this.transformItem(item)];
  };

  @action deselectAll = () => {
    this.selectedItems = [];
  };

  @action selectAll = async () => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      this.loading = true;

      const pagination: IPagination<IProductV4> = await client.get(`/v4/products`, {
        params: {
          per_page: PER_PAGE.MAXIMUM,
          name: this.searchValue,
          categories: this.fetchCategoriesParams,
          is_delete: 0,
        },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });

      const allItems: IDealItem[] = pagination.data.reduce((sum: IDealItem[], current: IDealItem) => {
        if (current.is_donation || current.is_size || current.is_required_modifiers || current.is_exclusions) return sum;
        return sum.concat(this.transformItem(current));
      }, []);

      this.selectedItems = [...arrayMerge(this.selectedItems, allItems, 'id')];
      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'ItemsForDealModalStore selectAll' });
    }
  };

  @action clickSave = () => {
    modalStore.itemsForDealModal?.onSave(this.selectedItems);
    this.closeModal();
  };

  @action closeModal = () => {
    modalStore.closeItemsForDealModal();
    this.clearStore();
  };
  private clearStore = () => {
    if (this.cancel) this.cancel('Cancelled');
  };
}

export const itemsForDealModalStore = new ItemsForDealModalStore();
